import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { ProjectTaskService } from 'src/app/private/services/project-task.service';
import { ProjectService } from 'src/app/private/services/project.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

const BLANK_ITEM = () => ({
  // Core
  title: '',
  description: '',
  item_type: 'task_item',
  status: 'todo',
  priority: 'medium',

  // People
  assignee_id: null,
  reviewer_id: null,

  // Effort
  estimated_minutes: null,
  actual_minutes: null,
  progress_percentage: 0,

  // Dates
  start_date: null,
  due_date: null,

  // Hierarchy / dependencies
  parent_item_id: null,
  blocked_by_item_id: null,

  // Misc
  sort_order: 0,
  is_completed: false,
  remarks: '',
});

@Component({
  selector: 'app-project-task-item',
  templateUrl: './project-task-item.component.html',
  styleUrls: ['./project-task-item.component.scss'],
})
export class ProjectTaskItemComponent implements OnInit, OnDestroy {
  @Input() taskId!: any;

  @Input() projectId!: any;

  private destroy$ = new Subject<void>();

  // ── Data ───────────────────────────────────────────────────────────────────
  items: any[] = [];

  users: any[] = [];

  // ── UI State ───────────────────────────────────────────────────────────────
  loading = false;
  itemBtnLoading = false;
  editBtnLoading = false;

  editingItemId: any = null;

  itemData: any = BLANK_ITEM();

  editItemData: any = {};

  // ── Lookup Maps ────────────────────────────────────────────────────────────
  readonly ITEM_TYPES = [
    { key: 'task_item', label: 'Task' },
    { key: 'research', label: 'Research' },
    { key: 'review', label: 'Review' },
    { key: 'deployment', label: 'Deployment' },
    { key: 'project_setup', label: 'Project Setup' },
    { key: 'client_setup', label: 'Client Setup' },
    { key: 'design', label: 'Design' },
    { key: 'content_creation', label: 'Content Creation' },
    { key: 'development', label: 'Development' },
    { key: 'testing', label: 'Testing' },
    { key: 'training', label: 'Training' },
    { key: 'maintenance', label: 'Maintenance' },
    { key: 'billing', label: 'Billing' },
    { key: 'feedback', label: 'Feedback' },
    { key: 'onboarding', label: 'Onboarding' },
    { key: 'demo', label: 'Demo' },
    { key: 'handover', label: 'Handover' },
    { key: 'configuration', label: 'Configuration' },
    { key: 'migration', label: 'Migration' },
    { key: 'integration', label: 'Integration' },
    { key: 'cloud_deployment', label: 'Cloud Deployment' },
    { key: 'other', label: 'Other' },
  ];

  readonly STATUSES = [
    { key: 'todo', label: 'To Do', color: '#9b51e0' },

    {
      key: 'development',
      label: 'Development',
      color: '#2d9cdb',
    },

    {
      key: 'ready_for_testing',
      label: 'Ready for Testing',
      color: '#f2994a',
    },

    {
      key: 'review',
      label: 'Review',
      color: '#bb6bd9',
    },

    {
      key: 'testing',
      label: 'Testing',
      color: '#219653',
    },

    {
      key: 'done',
      label: 'Done',
      color: '#27ae60',
    },
  ];

  readonly PRIORITIES = [
    {
      key: 'low',
      label: 'Low',
      color: '#6fcf97',
    },

    {
      key: 'medium',
      label: 'Medium',
      color: '#f2c94c',
    },

    {
      key: 'high',
      label: 'High',
      color: '#f2994a',
    },

    {
      key: 'urgent',
      label: 'Urgent',
      color: '#eb5757',
    },
  ];

  constructor(
    private api: ProjectTaskService,
    private projectService: ProjectService,
    private localStorage: LocalStorageService,
    private toastr: ToastrService,
    private modalService: NgbModal,
  ) { }

  ngOnInit() {
    this.loadItems();

    this.loadUsers();
  }

  ngOnDestroy() {
    this.destroy$.next();

    this.destroy$.complete();
  }

  // ── Load Items ─────────────────────────────────────────────────────────────

  loadItems() {
    this.loading = true;
    this.api
      .getAllTaskItems({
        client_id: this.localStorage.getClientId(),
        project_id: this.projectId,
        task_id: this.taskId,
        limit: 100,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.items = res?.rows ?? [];
          this.items.sort((a: any, b: any) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0));
          this.loading = false;
        },

        error: () => {
          this.loading = false;
          this.toastr.error('Failed to load task items');
        },
      });
  }

  // ── Load Users ─────────────────────────────────────────────────────────────

  loadUsers() {
    this.projectService
      .getProjectMembers({
        client_id: this.localStorage.getClientId(),
        project_id: this.projectId,
        user_id: this.localStorage.getUserId(),
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.users = this.projectService.parseProjectMembersResponse(res);
        },

        error: () => {
          this.toastr.error('Failed to load users');
        },
      });
  }

  // ── Add Item ───────────────────────────────────────────────────────────────

  openAddModal(content: any) {
    this.itemData = BLANK_ITEM();
    this.modalService.open(content, { size: 'lg', centered: true, backdrop: 'static' });
  }

  addItem() {
    if (!this.itemData.title?.trim()) return;

    if (
      this.itemData.start_date &&
      this.itemData.due_date &&
      new Date(this.itemData.start_date) > new Date(this.itemData.due_date)
    ) {
      this.toastr.error('Start date cannot be greater than Due date');
      return;
    }

    this.itemBtnLoading = true;

    const payload = {
      ...this.itemData,

      task_id: this.taskId,

      project_id: this.projectId,

      client_id: this.localStorage.getClientId(),

      created_by: this.localStorage.getUserId(),
    };

    this.api
      .createTaskItem(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.itemBtnLoading = false;
          this.itemData = BLANK_ITEM();
          this.modalService.dismissAll();
          this.toastr.success('Item added');
          this.loadItems();
        },

        error: () => {
          this.itemBtnLoading = false;
          this.toastr.error('Failed to add item');
        },
      });
  }

  // ── Edit Item ──────────────────────────────────────────────────────────────

  onStatusChange(status: string, dataObj: any) {
    if (status === 'todo') {
      dataObj.progress_percentage = 0;
    } else if (status === 'development') {
      dataObj.progress_percentage = 50;
    } else if (status === 'testing' || status === 'ready_for_testing') {
      dataObj.progress_percentage = 90;
    } else if (status === 'review') {
      dataObj.progress_percentage = 80;
    } else if (status === 'done') {
      dataObj.progress_percentage = 100;
      dataObj.is_completed = true;
    }
  }

  openEditModal(content: any, item: any) {
    this.editingItemId = item.item_id;

    this.editItemData = {
      ...item,

      due_date: item.due_date
        ? new Date(item.due_date).toISOString().substring(0, 10)
        : null,

      start_date: item.start_date
        ? new Date(item.start_date).toISOString().substring(0, 10)
        : null,
    };

    this.modalService.open(content, { size: 'lg', centered: true, backdrop: 'static' });
  }

  saveEdit() {
    if (!this.editItemData.title?.trim()) return;

    if (
      this.editItemData.start_date &&
      this.editItemData.due_date &&
      new Date(this.editItemData.start_date) > new Date(this.editItemData.due_date)
    ) {
      this.toastr.error('Start date cannot be greater than Due date');
      return;
    }

    this.editBtnLoading = true;

    const payload = {
      ...this.editItemData,

      where: {
        item_id: this.editingItemId,
      },

      updated_by: this.localStorage.getUserId(),

      client_id: this.localStorage.getClientId(),

      project_id: this.projectId,
    };

    this.api
      .updateTaskItem(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.editBtnLoading = false;
          this.editingItemId = null;
          this.modalService.dismissAll();
          this.toastr.success('Item updated');
          this.loadItems();
        },

        error: () => {
          this.editBtnLoading = false;
          this.toastr.error('Failed to update item');
        },
      });
  }

  cancelEdit() {
    this.editingItemId = null;
    this.modalService.dismissAll();
  }

  // ── Delete Item ────────────────────────────────────────────────────────────

  deleteItem(id: any) {
    this.api
      .deleteTaskItem({
        where: {
          item_id: id,
        },

        deleted_by: this.localStorage.getUserId(),
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastr.success('Item deleted');

          this.loadItems();
        },

        error: () => {
          this.toastr.error('Failed to delete item');
        },
      });
  }

  // ── Cycle Status ───────────────────────────────────────────────────────────

  cycleStatus(item: any) {
    const idx = this.STATUSES.findIndex((s) => s.key === item.status);

    const next = this.STATUSES[(idx + 1) % this.STATUSES.length];

    this.api
      .updateTaskItem({
        status: next.key,

        where: {
          item_id: item.item_id,
        },

        updated_by: this.localStorage.getUserId(),
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loadItems();
        },

        error: () => {
          this.toastr.error('Failed to update status');
        },
      });
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  getUserName(id: any): string {
    const u = this.users.find((u: any) => u.user_id === id || u.user_id == id);

    return u ? `${u.fname} ${u.lname}` : '—';
  }

  getStatusLabel(key: string): string {
    return this.STATUSES.find((s) => s.key === key)?.label ?? key;
  }

  getStatusColor(key: string): string {
    return this.STATUSES.find((s) => s.key === key)?.color ?? '#999';
  }

  getPriorityColor(key: string): string {
    return this.PRIORITIES.find((p) => p.key === key)?.color ?? '#999';
  }

  prettyMinutes(m: number): string {
    if (!m) return '—';

    const h = Math.floor(m / 60);

    const min = m % 60;

    return h > 0 ? `${h}h ${min > 0 ? min + 'm' : ''}`.trim() : `${min}m`;
  }

  isOverdue(dueDate: string): boolean {
    if (!dueDate) return false;

    return new Date(dueDate) < new Date();
  }
}
